"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useActionState, useEffect, useState } from "react";
import { signIn, SignInError } from "@/features/firebase/auth/signIn";
import { FormAction } from "@/features/common/type/commonType";
import { useRouter } from "next/navigation";
import { getCurrentRole } from "@/features/firebase/auth/actions/getCurrentRole";
import { signOut } from "@/features/firebase/auth/signOut";
import { Loader2 } from "lucide-react"; // Loader2アイコンをインポート
import { checkAgentIsPasswordReseted } from "@/features/agents/repositories/checkAgentIsPasswordReseted";
import { checkCompanyIsPasswordReseted } from "@/features/companies/repositories/checkCompanyIsPasswordReseted";

// ログインフォームの型定義
type LoginFormState = {
  email: string;
  password: string;
};

type LoginResult = { state: LoginFormState; error?: string; errorField?: "email" | "password" };

export function Login() {
  const router = useRouter();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handleSignIn: FormAction<null | LoginResult> = async (prevState, formData) => {
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    setIsAuthenticating(true);
    const result: SignInError | null = await signIn(email, password);

    // エラーがあった場合はローディング状態を解除
    if (result?.message) {
      setIsAuthenticating(false);
      return {
        state: { email, password },
        error: result.message,
        errorField: result.field,
      };
    }

    // 解約サイクル C (INACTIVE) の AGENT USER はログイン不可（仕様: docs/有料エージェント解約時の挙動.md 7-6）。
    // サーバー（createSessionAction）がセッションを出さず、signIn がエラーを返すのでここまで来ない

    // フォームの状態とエラーを返す
    return {
      state: {
        email,
        password,
      },
      error: undefined,
    };
  };

  const [formState, formAction, isPending] = useActionState<null | LoginResult, FormData>(
    handleSignIn,
    null,
  );

  // ページの初期化時にサインアウトを実行。この端末の Cookie を消すだけで、他の端末のセッションは切らない
  // （ログイン画面へのリンクを踏ませるだけで、全端末をログアウトさせられないように）
  useEffect(() => {
    async function handleSignOut() {
      const signOutError = await signOut({ allDevices: false });
      if (signOutError) {
        console.error("サインアウトに失敗しました:", signOutError);
      }
    }
    handleSignOut();
  }, []);

  // ログイン後のリダイレクト処理
  useEffect(() => {
    async function fetchRole() {
      // エラーがない場合のみロールを取得してリダイレクト
      if (formState && !formState.error) {
        try {
          const role = await getCurrentRole();
          if (role === "AGENT USER") {
            if (await checkAgentIsPasswordReseted()) {
              router.push("/a/top/new");
            } else {
              router.push("/reset-password");
            }
          } else if (role === "COMPANY USER") {
            if (await checkCompanyIsPasswordReseted()) {
              router.push("/c/top/new");
            } else {
              router.push("/reset-password");
            }
          } else if (role === "ADMIN") {
            router.push("/admin/top/new");
          } else {
            // ロールが取得できなかった場合はローディング状態を解除
            setIsAuthenticating(false);
          }
        } catch (error) {
          console.error("ロール取得エラー:", error);
          setIsAuthenticating(false);
        }
      }
    }

    if (formState && !isPending) {
      fetchRole();
    }
  }, [router, formState, isPending]);

  // ボタンを無効化する条件：フォーム送信中またはログイン認証中
  const isLoading = isPending || isAuthenticating;

  return (
    <div className="surface-panel flex flex-col items-center p-8 shadow-md">
      <div className="w-full space-y-8">
        <div className="flex flex-col items-center">
          <Image src="/byAgentLogo.svg" alt="byAgent" width={200} height={49} className="mb-8" />
          <h1 className="text-2xl font-bold">ログイン</h1>
        </div>

        {formState?.error && (
          <p role="alert" className="text-sm text-destructive">
            {formState.error}
          </p>
        )}

        <form action={formAction} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">メールアドレス</Label>
            <Input
              id="email"
              type="email"
              name="email"
              defaultValue={formState?.state?.email}
              placeholder="メールアドレスを入力してください"
              autoComplete="email"
              required
              aria-invalid={formState?.errorField === "email"}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">パスワード</Label>
              <Link
                href="/forgot-password"
                className="text-sm text-link transition-colors duration-fast hover:text-link-hover"
              >
                パスワードを忘れた方
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              name="password"
              defaultValue={formState?.state?.password}
              placeholder="パスワードを入力してください"
              autoComplete="current-password"
              required
              aria-invalid={formState?.errorField === "password"}
            />
          </div>

          <div className="py-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ログイン中...
                </span>
              ) : (
                "ログイン"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
